import { constrainSpeed, getSpeedLimits } from '@/pages/AdvancedEditor/utils/speedLimits';
import theme from '@/theme';
import { IVoice, Providers } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import { StyledSlider } from '../../common/StyledSlider';

const SpeedControlContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    gap: 4px;
    border: ${theme.colors.black[200]} 1px solid;
    border-radius: 8px;
`;
const SpeedText = styled.div`
    white-space: nowrap;
    font-size: 1.4rem;
    min-width: 130px;
`;

interface Props {
    ttsSelectedVoice: IVoice | null;
    value: number | string | undefined;
    setValue: (value: string) => void;
}

export const SpeedControl = ({ ttsSelectedVoice, value, setValue }: Props) => {
    const speed = +(value ?? 0);
    const prevProviderRef = useRef<Providers | undefined>(ttsSelectedVoice?.provider);

    const provider = ttsSelectedVoice?.provider || Providers.Microsoft;
    const { min: minSpeed, max: maxSpeed } = getSpeedLimits(provider);

    useEffect(() => {
        const currentProvider = ttsSelectedVoice?.provider;
        const providerChanged = prevProviderRef.current !== currentProvider;

        if (providerChanged && speed > 0 && currentProvider) {
            const constrainedSpeed = constrainSpeed(speed, currentProvider);
            if (constrainedSpeed !== speed) {
                setValue(constrainedSpeed.toString());
            }
        }

        prevProviderRef.current = currentProvider;
    }, [ttsSelectedVoice?.provider, speed, setValue]);

    const handleSpeedChange = (value: number) => {
        setValue(value.toString());
    };

    return (
        <SpeedControlContainer id="speed-container">
            <SpeedText>Speed ({speed}x)</SpeedText>
            <StyledSlider
                aria-label="Volume"
                value={speed}
                onChange={(_e, value) => handleSpeedChange(value as number)}
                step={0.1}
                min={minSpeed}
                max={maxSpeed}
            />
        </SpeedControlContainer>
    );
};
